import { Box, BoxProps, useColorModeValue } from '@chakra-ui/react'

import { SCOTT_AIR_SID } from '@common/util/constants'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { isProd } from '~/util/env'

interface LogoProps extends BoxProps {
  width?: number
}

const envToGradientColors = {
  prod: {
    light: ['#BB6FEA', '#5067DD'],
    dark: ['#D590FF', '#8598FF'],
  },
  dev: {
    light: ['#76BD75', '#4AB1C8'],
    dark: ['#9DF19C', '#5ED1EA'],
  },
} as const

export const Logo = (props: LogoProps) => {
  const user = useCurrentUser()
  const userIsScott = user?.sid === SCOTT_AIR_SID
  const env = isProd && !userIsScott ? 'prod' : 'dev'
  const mode = useColorModeValue('light', 'dark')
  const [color1, color2] = envToGradientColors[env][mode]
  return (
    <Box {...props} width={props.width ?? '100%'}>
      <LogoSvg color1={color1} color2={color2} />
    </Box>
  )
}

export const Mark = (props: LogoProps) => {
  const user = useCurrentUser()
  const userIsScott = user?.sid === SCOTT_AIR_SID
  const env = isProd && !userIsScott ? 'prod' : 'dev'

  const mode = useColorModeValue('light', 'dark')
  const [color1, color2] = envToGradientColors[env][mode]
  return (
    <Box {...props} width={props.width ?? '100%'}>
      <MarkSvg color1={color1} color2={color2} />
    </Box>
  )
}

const LogoSvg = ({ color1, color2 }: { color1: string; color2: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 564 96">
    <title>Rentana logo</title>
    <path
      fill="url(#rentanaLogoGradient)"
      d="M82.0507 14.0589C73.3644 5.37258 61.3644 0 48.1096 0 34.8547 0 22.8547 5.37258 14.1684 14.0589 5.48214 22.7452.10956 34.7452.10956 48c0 11.0449 3.73041 21.2185 10.00004 29.3296v-17.33l16-8v38.6731a47.74238 47.74238 0 0 0 4 1.8382V45.9996l3 .0002v-3h10v2.9998l3 .0002v49.9593c.6632.0272 1.33.0409 2 .0409.6699 0 1.3367-.0137 2-.0409V39.9996l8-4 8 4v52.5113a47.7571 47.7571 0 0 0 4-1.8382V45.9996l8 .0002v3.9998h7v28.5802c6.8709-8.3041 11-18.9597 11-30.5798 0-13.2548-5.3726-25.2548-14.0589-33.9411Zm58.6273 60.4409h3.948c1.092 0 1.68-.588 1.68-1.68v-19.74c0-.588.252-.84.84-.84h16.968c2.184 0 2.772.504 3.696 2.352l9.408 18.48c.42.924.924 1.428 2.016 1.428h4.368c1.26 0 1.848-.672 1.26-1.848l-11.004-21.588c-.42-.924-.42-1.428.168-2.016l5.544-5.544c2.688-2.688 3.444-4.704 3.444-7.812v-5.208c0-3.024-.756-5.124-3.444-7.812l-3.276-3.276c-2.688-2.688-4.788-3.696-8.904-3.696h-26.712c-1.092 0-1.596.588-1.596 1.68v55.44c0 1.092.504 1.68 1.596 1.68Zm6.468-28.728c-.588 0-.84-.252-.84-.84v-21.924c0-.588.252-.84.84-.84h19.488c1.932 0 3.192.504 4.536 1.848l2.856 2.856c1.428 1.428 1.68 2.268 1.68 4.536v3.36c0 2.268-.252 3.108-1.68 4.536l-4.62 4.704c-1.344 1.26-2.604 1.764-4.536 1.764h-17.724Zm58.533 28.728h35.952c1.176 0 1.68-.588 1.68-1.68v-3.192c0-1.092-.504-1.596-1.68-1.596h-29.484c-.588 0-.84-.252-.84-.84v-19.068c0-.588.252-.84.84-.84h25.452c1.092 0 1.68-.504 1.68-1.68v-3.024c0-1.092-.588-1.596-1.68-1.596h-25.452c-.588 0-.84-.252-.84-.84v-17.136c0-.588.252-.84.84-.84h29.484c1.176 0 1.68-.504 1.68-1.596v-3.192c0-1.092-.504-1.68-1.68-1.68h-35.952c-1.092 0-1.596.588-1.596 1.68v55.44c0 1.092.504 1.68 1.596 1.68Zm59.752 0h3.78c1.176 0 1.68-.588 1.68-1.68v-44.856c0-.588.588-.756.924-.252l26.46 45.108c.84 1.344 1.512 1.68 2.856 1.68h5.208c1.176 0 1.764-.588 1.764-1.68v-55.44c0-1.092-.588-1.68-1.764-1.68h-3.696c-1.176 0-1.68.588-1.68 1.68v44.856c0 .588-.588.756-.924.252l-26.46-45.108c-.84-1.344-1.428-1.68-2.772-1.68h-5.376c-1.092 0-1.596.588-1.596 1.68v55.44c0 1.092.504 1.68 1.596 1.68Zm104.696-52.332c1.176 0 1.68-.504 1.68-1.596v-3.192c0-1.092-.504-1.68-1.68-1.68h-42.168c-1.176 0-1.68.588-1.68 1.68v3.192c0 1.092.504 1.596 1.68 1.596h16.632c.588 0 .84.252.84.84v49.812c0 1.092.504 1.68 1.68 1.68h3.864c1.092 0 1.68-.588 1.68-1.68v-49.812c0-.588.252-.84.84-.84h16.632Zm51.185 34.272c.588 0 .756.252 1.008.84l4.956 14.952c.504 1.764 1.092 2.268 2.52 2.268h3.444c1.596 0 2.016-.756 1.512-2.268l-18.312-54.18c-.588-1.68-1.092-2.352-2.94-2.352h-6.804c-1.848 0-2.436.672-3.024 2.352l-18.312 54.18c-.504 1.512 0 2.268 1.512 2.268h3.36c1.428 0 2.016-.504 2.604-2.268l4.956-14.952c.252-.588.42-.84 1.008-.84h22.512Zm-20.412-6.048c-.504 0-.756-.336-.588-.756l9.24-27.552c.168-.588.756-.588 1.008 0l9.24 27.552c.168.42-.084.756-.588.756H400.9Zm53.137 24.108h3.78c1.176 0 1.68-.588 1.68-1.68v-44.856c0-.588.588-.756.924-.252l26.46 45.108c.84 1.344 1.512 1.68 2.856 1.68h5.208c1.176 0 1.764-.588 1.764-1.68v-55.44c0-1.092-.588-1.68-1.764-1.68h-3.696c-1.176 0-1.68.588-1.68 1.68v44.856c0 .588-.588.756-.924.252l-26.46-45.108c-.84-1.344-1.428-1.68-2.772-1.68h-5.376c-1.092 0-1.596.588-1.596 1.68v55.44c0 1.092.504 1.68 1.596 1.68Zm96.212-18.06c.588 0 .756.252 1.008.84l4.956 14.952c.504 1.764 1.092 2.268 2.52 2.268h3.444c1.596 0 2.016-.756 1.512-2.268l-18.312-54.18c-.588-1.68-1.092-2.352-2.94-2.352h-6.804c-1.848 0-2.436.672-3.024 2.352l-18.312 54.18c-.504 1.512 0 2.268 1.512 2.268h3.36c1.428 0 2.016-.504 2.604-2.268l4.956-14.952c.252-.588.42-.84 1.008-.84h22.512Zm-20.412-6.048c-.504 0-.756-.336-.588-.756l9.24-27.552c.168-.588.756-.588 1.008 0l9.24 27.552c.168.42-.084.756-.588.756h-18.312Z"
    />
    <defs>
      <linearGradient id="rentanaLogoGradient" x1="0" x2="100%" y1="100%" y2="0">
        <stop stopColor={color1} />
        <stop offset="1" stopColor={color2} />
      </linearGradient>
    </defs>
  </svg>
)

const MarkSvg = ({ color1, color2 }: { color1: string; color2: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 96 96">
    <title>Rentana logo</title>
    <path
      fill="url(#rentanaMarkGradient)"
      fillRule="evenodd"
      d="M81.9411 14.0591C73.2548 5.3728 61.2548.00022 48 .00022S22.7452 5.3728 14.0589 14.0591C5.37258 22.7454 0 34.7454 0 48.0002c0 11.0449 3.73041 21.2185 10 29.3296v-17.33l16-8V90.673a47.75627 47.75627 0 0 0 4 1.8381V45.9998L33 46v-3h10v2.9998L46 46v49.9593c.6633.0272 1.33.0409 2 .0409.67 0 1.3367-.0137 2-.0409V39.9998l8-4 8 4v52.5113a47.74155 47.74155 0 0 0 4-1.8381V45.9998L78 46v3.9998h7V78.58c6.8709-8.3041 11-18.9597 11-30.5798 0-13.2548-5.3726-25.2548-14.0589-33.9411Z"
      clipRule="evenodd"
    />
    <defs>
      <linearGradient id="rentanaMarkGradient" x1="0" x2="100%" y1="100%" y2="0">
        <stop stopColor={color1} />
        <stop offset="1" stopColor={color2} />
      </linearGradient>
    </defs>
  </svg>
)
